import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getPageBySlug, getGalleryBySlug } from '../lib/cms';
import { useSEO } from '../hooks/useSEO';

export default function Cakes() {
  const [pageData, setPageData] = useState(null);
  const [galleryData, setGalleryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);

  useEffect(() => {
    async function loadData() {
      const [page, gallery] = await Promise.all([
        getPageBySlug('kager'),
        getGalleryBySlug('kager')
      ]);
      setPageData(page);
      setGalleryData(gallery);
      setLoading(false);
    }
    loadData();
  }, []);

  useSEO({
    title: pageData?.meta_title || 'Kager & konditori · Vissenbjerg Bageri',
    description: pageData?.meta_description || 'Lækre kager, flødekager, onsdagssnegle og eksklusive bryllupskager hos Vissenbjerg Bageri. Vi har over 20 års erfaring som konditor. Besøg os.'
  });

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center bg-cream">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-gold-brown border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="bg-transparent text-chocolate py-12 md:py-20">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4 mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-brown">
            Vores Konditori
          </span>
          <h1 className="font-serif text-4xl font-bold sm:text-5xl text-chocolate">
            Kager & Konditori
          </h1>
          <div className="h-1 w-20 bg-gold-brown mx-auto rounded-full" />
        </div>

        {/* Content Section */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate">
              Kager og søde sager til enhver lejlighed
            </h2>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vissenbjerg bageri er det rette sted, hvis du søger wienerbrød, kager eller andre søde sager til lækkersulten eller som en ekstra lækker godbid til aftenkaffen. Vi har et stort udvalg af alt fra lækre flødekager, appetitlige kringler og farverige kager til vores populære onsdagssnegle, delikate desserter og mange andre lækre smagsoplevelser.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Hos Vissenbjerg bageri stræber vi efter at kunne tilbyde vores kunder et bredt udvalg, hvorfor vi også lægger stor vægt på udskiftning i vores bagværk. Derfor kan vi tilbyde kager, der matcher årstiden, så du kan blive inspireret og fristet på ny, hver gang du træder ind i vores bageri.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vi tilbyder naturligvis også lækre og velsmagende glutenreducerede kager samt kager bagt uden sukker, så kom ind forbi vores butik og bliv fristet. Du kan også forudbestille en kage ved at ringe på telefon <a href="tel:64471266" className="font-semibold text-gold-brown hover:underline">64 47 12 66</a>.
            </p>
          </div>
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gold-brown/10 h-[450px] relative">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/b8-1.jpg"
              alt="Lækre kager"
              className="w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Highlight Brunsviger Section */}
        <div className="rounded-2xl bg-chocolate text-cream p-8 md:p-12 shadow-xl border border-gold-brown/20 flex flex-col lg:flex-row items-center gap-10 mb-20">
          <div className="flex-shrink-0 w-full lg:w-80 h-56 rounded-xl overflow-hidden shadow-md">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=600,height=400,fit=cover,quality=80,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/brunsviger-kagemand-kagedame.jpg"
              alt="Fynsk Brunsviger"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-4 flex-grow">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-gold-brown">
              Fyns Nationalspise
            </span>
            <h2 className="font-serif text-3xl font-bold text-cream">Den helt rigtige fynske brunsviger</h2>
            <p className="text-sm text-cream/80 leading-relaxed">
              Især er vores hjemmelavede brunsviger et meget populært bagværk hos vores kunder – så populært, at vi har leveret vores kagemand hele vejen til et arrangement på Christiansborg. Vi bager den helt rigtige fynske brunsviger til alle formål.
            </p>
            <div className="pt-2 flex flex-wrap items-center gap-3">
              <Link
                to="/brunsviger"
                className="inline-flex items-center gap-2 rounded-md bg-gold-brown px-5 py-2.5 text-sm font-semibold text-cream hover:bg-copper transition-colors duration-200 shadow-sm"
              >
                Læs mere om vores brunsviger <i className="fa-solid fa-arrow-right" />
              </Link>
              <a
                href="https://brunsvigermand.dk/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-md border border-cream/30 bg-cream/10 px-5 py-2.5 text-sm font-semibold text-cream hover:bg-cream hover:text-chocolate transition-all duration-200"
              >
                Bestil på brunsvigermand.dk <i className="fa-solid fa-arrow-up-right-from-square text-xs" />
              </a>
            </div>
          </div>
        </div>

        {/* Konditor & Festlagkager */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
          <div className="rounded-2xl overflow-hidden shadow-xl border border-gold-brown/10 h-[450px] order-last lg:order-first">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/6b7bbefe-5a4a-4ac7-84b7-5528a226ac44/img-0288.jpg"
              alt="Smukke konditorkager"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="space-y-6">
            <h2 className="font-serif text-2xl md:text-3xl font-bold text-chocolate">
              Smukke konditor- og festlagkager
            </h2>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Har du brug for en anderledes kage til en speciel anledning, kan Vissenbjerg bageri klare opgaven. Hvad enten du står for døren til en 30-års fødselsdag, en firmafest eller en konfirmation, kan vi levere smagfulde, lækre og smukke æstetiske fest- og konditorkager.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Vi har mere end 20 års erfaring som konditor og har gennem årene leveret konditorkager til enhver anledning. I øjeblikket oplever vi blandt andet en stor stigning i baby shower kager, hvor vi sammen med vores kunder finder frem til den helt rigtige udformning, stil og smagsvariant.
            </p>
            <p className="text-base text-chocolate/80 leading-relaxed">
              Mangler du den gode ide, har vi et stort katalog med forskellige kager, så du kan blive inspireret til, hvordan netop din kage skal se ud.
            </p>
            <h3 className="font-serif text-xl font-bold text-chocolate pt-2">
              Eksklusive bryllupskager
            </h3>
            <p className="text-base text-chocolate/80 leading-relaxed">
              I samråd med vores kunder skaber vi individuelle, unikke og luksuriøse bryllupskager, der er med til at symbolisere det indgåede ægteskab og kærligheden mellem mand og kvinde. Med en bryllupskage er kvaliteten, smagen og det æstetiske design naturligvis i højsædet og vi sørger for en delikat kage efter dine ønsker.
            </p>
          </div>
        </div>

        {/* Technical Connected Gallery */}
        {galleryData && galleryData.images && galleryData.images.length > 0 && (
          <div className="space-y-8">
            <div className="text-center max-w-2xl mx-auto space-y-2">
              <h2 className="font-serif text-3xl font-bold text-chocolate">Kagegalleri</h2>
              <p className="text-sm text-chocolate/60">
                Se et udvalg af vores smukke kager, flødekager, wienerbrød og konditorkunst. Klik på et billede for at se det i stor visning.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryData.images.map((imgUrl, index) => (
                <button
                  key={index}
                  onClick={() => setActiveImage(imgUrl)}
                  className="group relative h-64 sm:h-80 md:h-96 overflow-hidden rounded-xl bg-chocolate border border-gold-brown/10 focus:outline-none focus:ring-2 focus:ring-gold-brown"
                >
                  <img
                    src={`https://assets.auxodev.dk/cdn-cgi/image/width=600,height=800,fit=cover,quality=85,format=auto/${imgUrl.replace('https://assets.auxodev.dk/', '')}`}
                    alt={`Kager galleri ${index + 1}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-chocolate/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <i className="fa-solid fa-magnifying-glass-plus text-cream text-2xl drop-shadow-md" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Lightbox / Stor visning */}
        {activeImage && (
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-chocolate/95 p-4 backdrop-blur-sm animate-fadeIn"
            onClick={() => setActiveImage(null)}
          >
            <button
              onClick={() => setActiveImage(null)}
              className="absolute top-4 right-4 text-cream/80 hover:text-white text-3xl focus:outline-none"
              aria-label="Luk visning"
            >
              <i className="fa-solid fa-xmark" />
            </button>
            <div
              className="relative max-w-4xl max-h-[90vh] overflow-hidden rounded-lg shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={activeImage}
                alt="Stor visning"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
